import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { PageHeader, StatCard, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { TripStatus, PAY_MODES, downloadCsv, nativeInput } from "@/components/market";

function Filters({ f, setF, vendors, extra }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-4">
      <Field label="Vendor">
        <select data-testid="report-vendor-filter" className={nativeInput} value={f.vendor_id} onChange={set("vendor_id")}>
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </Field>
      <Field label="From"><Input type="date" data-testid="report-from" value={f.date_from} onChange={set("date_from")} /></Field>
      <Field label="To"><Input type="date" data-testid="report-to" value={f.date_to} onChange={set("date_to")} /></Field>
      {extra(set)}
    </div>
  );
}

function useReport(path, f) {
  const [data, setData] = useState({ rows: [], totals: {} });
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get(path, { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [path, f]);
  return data;
}

const FREIGHT_COLS = [["vendor_name", "Vendor"], ["vehicle_number", "Vehicle"], ["lr_number", "LR"], ["booking_date", "LR Date"], ["gross_freight", "Gross Freight"], ["tds", "TDS"], ["mamul", "Mamul"], ["shortage", "Shortage Debit"], ["detention", "Detention"], ["net_payable", "Net Payable"], ["paid", "Paid"], ["outstanding", "Outstanding"], ["status", "Status"]];
const MONEY = new Set(["gross_freight", "tds", "mamul", "shortage", "detention", "net_payable", "paid", "outstanding", "amount", "outstanding_after"]);

function FreightReport({ vendors }) {
  const [f, setF] = useState({ vendor_id: "", date_from: "", date_to: "", status: "" });
  const { rows, totals } = useReport("/market/reports/freight", f);
  const download = () => downloadCsv(`Vendor_Hiring_Freight_${today()}.csv`, FREIGHT_COLS.map(([, l]) => l),
    [...rows.map((r) => FREIGHT_COLS.map(([k]) => r[k])), FREIGHT_COLS.map(([k], i) => (i === 0 ? "TOTAL" : MONEY.has(k) ? totals[k] : ""))]);
  return (
    <div>
      <Filters f={f} setF={setF} vendors={vendors} extra={(set) => (
        <Field label="Trip Status">
          <select data-testid="report-status-filter" className={nativeInput} value={f.status} onChange={set("status")}>
            <option value="">All</option>{["Active", "POD Verified", "Settled"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      )} />
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Gross Freight" value={inr(totals.gross_freight)} testid="freight-total-gross" />
        <StatCard label="TDS Deducted" value={inr(totals.tds)} testid="freight-total-tds" />
        <StatCard label="Net Payable" value={inr(totals.net_payable)} testid="freight-total-net" />
        <StatCard label="Outstanding" value={inr(totals.outstanding)} accent="text-rose-600" testid="freight-total-outstanding" />
      </div>
      <div className="mb-3 flex justify-end"><Button variant="outline" size="sm" data-testid="download-freight-report-btn" disabled={!rows.length} onClick={download}><Download className="mr-1 h-4 w-4" />Download Excel / CSV</Button></div>
      <TableWrap>
        <thead><tr>{FREIGHT_COLS.map(([, l]) => <th key={l} className={th}>{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.trip_id} className={tr} data-testid={`freight-report-row-${r.trip_id}`}>
              {FREIGHT_COLS.map(([k]) => (
                <td key={k} className={`${td} ${MONEY.has(k) ? "amount" : ""}`}>
                  {k === "status" ? <TripStatus status={r.status} /> : k === "lr_number" ? <Link className="font-semibold text-primary hover:underline" to={`/market/trips/${r.trip_id}`}>{r.lr_number}</Link> : k === "booking_date" ? fmtDate(r[k]) : MONEY.has(k) ? inr(r[k], 2) : r[k]}
                </td>
              ))}
            </tr>
          ))}
          {!!rows.length && <tr className="border-t bg-secondary/60 font-semibold" data-testid="freight-report-total-row">{FREIGHT_COLS.map(([k], i) => <td key={k} className={`${td} amount`}>{i === 0 ? "TOTAL" : MONEY.has(k) ? inr(totals[k], 2) : ""}</td>)}</tr>}
        </tbody>
      </TableWrap>
      {!rows.length && <Empty testid="freight-report-empty" />}
    </div>
  );
}

const PAY_COLS = [["date", "Payment Date"], ["vendor_name", "Vendor"], ["lr_number", "LR"], ["vehicle_number", "Vehicle"], ["kind", "Type"], ["mode", "Mode"], ["ref_id", "Transaction Ref"], ["amount", "Amount"], ["net_payable", "Trip Net Payable"], ["outstanding_after", "Outstanding After"]];

function PaymentReport({ vendors }) {
  const [f, setF] = useState({ vendor_id: "", date_from: "", date_to: "", mode: "" });
  const { rows, totals } = useReport("/market/reports/payments", f);
  const val = (r, k) => (k === "kind" ? (r.kind === "advance" ? "Advance" : "Final Balance") : r[k]);
  const download = () => downloadCsv(`Vendor_Payments_${today()}.csv`, PAY_COLS.map(([, l]) => l),
    [...rows.map((r) => PAY_COLS.map(([k]) => val(r, k))), ["TOTAL PAID", "", "", "", "", "", "", totals.paid, "Outstanding", totals.outstanding]]);
  return (
    <div>
      <Filters f={f} setF={setF} vendors={vendors} extra={(set) => (
        <Field label="Payment Mode">
          <select data-testid="report-mode-filter" className={nativeInput} value={f.mode} onChange={set("mode")}>
            <option value="">All</option>{PAY_MODES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      )} />
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Total Paid" value={inr(totals.paid)} testid="pay-total-paid" />
        <StatCard label="Advances" value={inr(totals.advance)} testid="pay-total-advance" />
        <StatCard label="Final Balance Payouts" value={inr(totals.final)} testid="pay-total-final" />
        <StatCard label="Outstanding Payable" value={inr(totals.outstanding)} accent="text-rose-600" testid="pay-total-outstanding" />
      </div>
      <div className="mb-3 flex justify-end"><Button variant="outline" size="sm" data-testid="download-payment-report-btn" disabled={!rows.length} onClick={download}><Download className="mr-1 h-4 w-4" />Download Excel / CSV</Button></div>
      <TableWrap>
        <thead><tr>{PAY_COLS.map(([, l]) => <th key={l} className={th}>{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={tr} data-testid={`payment-report-row-${r.id}`}>
              {PAY_COLS.map(([k]) => <td key={k} className={`${td} ${MONEY.has(k) ? "amount" : ""}`}>{k === "date" ? fmtDate(r.date) : MONEY.has(k) ? inr(r[k], 2) : val(r, k) || "-"}</td>)}
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!rows.length && <Empty testid="payment-report-empty" />}
    </div>
  );
}

export default function MarketReports() {
  const [vendors, setVendors] = useState([]);
  useEffect(() => { api.get("/market/vendors").then((r) => setVendors(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <PageHeader title="Vendor Reports" subtitle="Hiring freight & vendor payout ledger for accounting audit" />
      <Tabs defaultValue="freight">
        <TabsList>
          <TabsTrigger value="freight" data-testid="tab-freight-report">Vendor Hiring Freight</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payment-report">Payments & Outstanding</TabsTrigger>
        </TabsList>
        <TabsContent value="freight"><FreightReport vendors={vendors} /></TabsContent>
        <TabsContent value="payments"><PaymentReport vendors={vendors} /></TabsContent>
      </Tabs>
    </div>
  );
}
